import { detectFileType, type ImportFileType } from '@/pages/library/components/import-file'

import { MAX_DATA_FILE_BYTES, MAX_PDF_BYTES, type AttachRole } from './attachment-limits'

export type AttachmentRoute =
  | { kind: 'image' }
  | { kind: 'document' }
  | { kind: 'data'; fileType: ImportFileType }
  | { kind: 'refused'; reason: string }

const IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/gif'])
const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'webp', 'gif'])

/** GIS files the assistant names but the Library cannot hold yet, so they are refused with a reason. */
const UNSUPPORTED_DATA_EXTENSIONS = new Set(['xlsx', 'xls', 'kml', 'kmz'])

function extensionOf(name: string): string {
  return name.toLowerCase().split('.').pop() ?? ''
}

export function isImageFile(file: Pick<File, 'name' | 'type'>): boolean {
  if (file.type) return IMAGE_TYPES.has(file.type)

  return IMAGE_EXTENSIONS.has(extensionOf(file.name))
}

function isPdfFile(file: Pick<File, 'name' | 'type'>): boolean {
  return file.type === 'application/pdf' || (!file.type && extensionOf(file.name) === 'pdf')
}

function megabytes(bytes: number): string {
  return `${Math.round((bytes / (1024 * 1024)) * 10) / 10} MB`
}

/**
 * Where a picked file goes — or why it cannot be attached. Pictures are open to everyone; a PDF needs a
 * signed-in user; a data file creates a Library item, so it needs write access.
 */
export function classifyAttachment(file: Pick<File, 'name' | 'type' | 'size'>, role: AttachRole): AttachmentRoute {
  if (isImageFile(file)) return { kind: 'image' }

  if (isPdfFile(file)) {
    if (role === 'guest') return { kind: 'refused', reason: 'Sign in to attach PDFs' }
    if (file.size > MAX_PDF_BYTES) {
      return { kind: 'refused', reason: `PDFs can be at most ${megabytes(MAX_PDF_BYTES)}` }
    }

    return { kind: 'document' }
  }

  const fileType = detectFileType(file.name)

  if (fileType) {
    if (role !== 'admin' && role !== 'publisher') {
      return { kind: 'refused', reason: 'Data files need write access to the Library' }
    }

    if (file.size > MAX_DATA_FILE_BYTES) return { kind: 'refused', reason: 'Files can be at most 1 GB' }

    return { kind: 'data', fileType }
  }

  if (UNSUPPORTED_DATA_EXTENSIONS.has(extensionOf(file.name))) {
    return { kind: 'refused', reason: 'The Library cannot take this file type yet' }
  }

  return { kind: 'refused', reason: "This file type can't be attached" }
}
